import { Box, Divider, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import TooltipIcon from 'modules/shift_management/components/ShiftManagement/TooltipIcon'
import { useCurrentViewOptions } from 'modules/shift_management/hooks/useCurrentViewOptions'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import type UnassignedShift from 'modules/shift_management/store/internal/models/UnassignedShift'
import TooltipLabel from 'modules/time_planning/components/Breaks/TooltipLabel'
import type PlannedBreak from 'modules/time_planning/store/internal/models/PlannedBreak'

import ShiftTime from './ShiftTime'

interface Props {
  unassignedShift: UnassignedShift
  plannedBreaks?: PlannedBreak[]
}

const CompactView = observer(({ unassignedShift }: Props) => {
  const { defaultShiftTitle } = unassignedShift

  return (
    <Box flexDirection="row" alignItems="center" width="full">
      {!!defaultShiftTitle ? (
        <Text.Normal size="100" weight="bold" color="grey1000" ellipsis>
          {defaultShiftTitle}
        </Text.Normal>
      ) : (
        <ShiftTime unassignedShift={unassignedShift} />
      )}
    </Box>
  )
})

const ShiftDetails = ({ unassignedShift, plannedBreaks }: Props) => {
  const { defaultShiftTitle, location, workArea, notes, isOvertime } = unassignedShift
  const [{ view }] = useSchedule()
  const { viewOptions } = useCurrentViewOptions()
  const {
    coverage: {
      showCompactView,
      showName,
      showWorkplace,
      showWorkArea,
      showOvertime,
      showBreaks,
      showNotes,
    },
  } = viewOptions

  const hasOvertime = view.isFullVersion && isOvertime
  const hasLocation = view.isByEmployees && view.isFullVersion && location
  const hasWorkArea = hasLocation && workArea

  if (showCompactView) {
    return <CompactView unassignedShift={unassignedShift} />
  }

  return (
    <Box flexDirection="column" gap="s2" minWidth="s40">
      {showOvertime && hasOvertime && (
        <Box padding="s2">
          <Text caps size="50" weight="bold" color="grey1000">
            {i18n.t('shift_management.overtime_tag')}
          </Text>
        </Box>
      )}

      {showName && !!defaultShiftTitle && (
        <Box flexDirection="row" alignItems="center" width="full">
          <Text.Normal size="100" weight="bold" color="grey1000" ellipsis>
            {defaultShiftTitle}
          </Text.Normal>
        </Box>
      )}

      <Box flexDirection="row" alignItems="center" width="full">
        <ShiftTime unassignedShift={unassignedShift} />
      </Box>
      {showWorkplace && hasLocation && (
        <Box overflowX="hidden">
          <Text size="100" color="grey1000" ellipsis>
            {location.attributes.name}
          </Text>
        </Box>
      )}
      <Box flexDirection="row" gap="s4" alignItems="center" paddingBottom="s2">
        {showBreaks && !!plannedBreaks?.length && (
          <TooltipIcon.Small
            icon="coffee"
            label={
              <Box flexDirection="column" gap="s4">
                {plannedBreaks.length > 1 && (
                  <Text size="100" color="grey000White">
                    {plannedBreaks.length} {i18n.t('company_settings.time.breaks.title')}:
                  </Text>
                )}
                {plannedBreaks.map((plannedBreak) => (
                  <>
                    {plannedBreaks.length > 1 && <Divider backgroundColor="grey1000white" />}
                    <Box key={plannedBreak.id} gap="s4">
                      <TooltipLabel plannedBreak={plannedBreak} timezone="Utc" size="100" />
                    </Box>
                  </>
                ))}
              </Box>
            }
          />
        )}
        {showNotes && !!notes && <TooltipIcon.Small icon="comment" label={notes} />}
        {showWorkArea && hasWorkArea && (
          <TooltipIcon.Small icon="space-control" label={workArea.attributes.name} />
        )}
      </Box>
    </Box>
  )
}

export default observer(ShiftDetails)
